import React from 'react';
import { LayoutChangeEvent, ListRenderItem, Platform, StyleSheet, View } from 'react-native';

import { RangeCalendarModal } from '@components/Calendar';
import Chip from '@components/Chip';
import Divider from '@components/Divider';
import {
  getFilterKey,
  getFilterLabel,
  getSearchResultKey,
  getSearchResults,
} from '@components/Explorer/search';
import type {
  ExplorerDisplayMode,
  PlaceSearchResult,
  SearchResult,
  SearchResultFilter,
  SearchTargets,
  SelectedSearchFilter,
} from '@components/Explorer/search';
import FlatList from '@components/FlatList';
import Icon from '@components/Icon';
import { PressableOpacity } from '@components/Pressable';
import ScrollView from '@components/ScrollView';
import SearchBar from '@components/SearchBar';
import Surface from '@components/Surface';
import { Caption, Paragraph } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { PlaceAutocompleteSelection } from '@db/Places';
import { dateRangeToString } from '@helpers/Dates';
import { DateRange, GroupSearchResult, UserSearchResult } from '@types';

const DISPLAY_MODE_CHIPS: {
  icon: React.ComponentProps<typeof Chip>['icon'];
  label: string;
  mode: ExplorerDisplayMode;
  testID: string;
}[] = [
  {
    icon: 'pin-outline',
    label: 'Visited',
    mode: 'visited',
    testID: 'components.Map.Search.VisitedChip',
  },
  {
    icon: 'sign-direction',
    label: 'Upcoming',
    mode: 'upcoming',
    testID: 'components.Map.Search.UpcomingChip',
  },
  {
    icon: 'home-outline',
    label: 'Residents',
    mode: 'residences',
    testID: 'components.Map.Search.ResidencesChip',
  },
];

type SearchHeaderProps = {
  header?: React.ReactNode;
  searchText: string;
  selectedFilters: SelectedSearchFilter[];
  displayMode?: ExplorerDisplayMode;
  dateRange?: DateRange;
  usersLoading: boolean;
  groupsLoading: boolean;
  placesLoading: boolean;
  placeSelectionLoading: boolean;
  searchTargets: SearchTargets;
  visibleUserResults: UserSearchResult[];
  visibleGroupResults: GroupSearchResult[];
  placeResults: PlaceSearchResult[];
  setSearchText: (text: string) => void;
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedSearchFilter[]>>;
  setDisplayMode: React.Dispatch<React.SetStateAction<ExplorerDisplayMode | undefined>>;
  setDateRange: (range: DateRange | undefined) => void;
  onPlaceSelect?: (selection: PlaceAutocompleteSelection) => void;
  onControlsLayout?: (height: number) => void;
  selectFilter: (filter: SearchResultFilter) => Promise<PlaceAutocompleteSelection | undefined>;
};

export function SearchHeader({
  header,
  searchText,
  selectedFilters,
  displayMode,
  dateRange,
  usersLoading,
  groupsLoading,
  placesLoading,
  placeSelectionLoading,
  searchTargets,
  visibleUserResults,
  visibleGroupResults,
  placeResults,
  setSearchText,
  setSelectedFilters,
  setDisplayMode,
  setDateRange,
  onPlaceSelect,
  onControlsLayout,
  selectFilter,
}: SearchHeaderProps) {
  const appTheme = useAppTheme();
  const [datePickerVisible, setDatePickerVisible] = React.useState(false);
  const searchResults = React.useMemo(
    () =>
      getSearchResults(
        visibleUserResults,
        visibleGroupResults,
        placeResults,
        searchTargets,
        selectedFilters
      ),
    [visibleUserResults, visibleGroupResults, placeResults, searchTargets, selectedFilters]
  );
  const loading =
    (searchTargets.users && usersLoading) ||
    (searchTargets.groups && groupsLoading) ||
    (searchTargets.places && placesLoading);
  const showNoResults = searchText && !loading && searchResults.length === 0;
  const dateFilterLabel = dateRange ? dateRangeToString(dateRange) : 'Dates';
  const searchPlaceholder = getSearchPlaceholder(searchTargets);
  const toggleDisplayMode = (mode: ExplorerDisplayMode) => {
    setDisplayMode((currentMode) => (currentMode === mode ? undefined : mode));
  };
  const handleControlsLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      onControlsLayout?.(event.nativeEvent.layout.height);
    },
    [onControlsLayout]
  );

  const handleSearchResultPress = async (result: SearchResult) => {
    if (placeSelectionLoading) return;

    const selectedPlaceSelection = await selectFilter(result.filter);
    if (selectedPlaceSelection) {
      onPlaceSelect?.(selectedPlaceSelection);
    }
  };

  const renderSearchResult: ListRenderItem<SearchResult> = ({ item }) => (
    <PressableOpacity
      accessibilityState={{ disabled: placeSelectionLoading }}
      disabled={placeSelectionLoading}
      onPress={() => void handleSearchResultPress(item)}>
      <View style={styles.resultRow}>
        <Icon name={item.icon} size={20} style={styles.resultIcon} />
        <View style={styles.resultTextContainer}>
          <Paragraph style={styles.resultText}>{item.label}</Paragraph>
          {item.detail ? <Caption style={styles.resultDetail}>{item.detail}</Caption> : null}
        </View>
      </View>
    </PressableOpacity>
  );

  return (
    <View>
      <View onLayout={handleControlsLayout} testID="components.Explorer.SearchHeader.Controls">
        {header}
        <SearchBar
          value={searchText}
          onChangeText={setSearchText}
          loading={loading}
          placeholder={searchPlaceholder}
          style={styles.searchBar}
        />
        <ScrollView horizontal contentContainerStyle={styles.selectedFilterContent}>
          {selectedFilters.map((filter) => (
            <Chip
              key={getFilterKey(filter)}
              onClose={() =>
                setSelectedFilters((currentFilters) =>
                  currentFilters.filter(
                    (currentFilter) => getFilterKey(currentFilter) !== getFilterKey(filter)
                  )
                )
              }>
              {getFilterLabel(filter)}
            </Chip>
          ))}
          {DISPLAY_MODE_CHIPS.map((chip) => (
            <Chip
              key={chip.mode}
              icon={chip.icon}
              selected={displayMode === chip.mode}
              onPress={() => toggleDisplayMode(chip.mode)}
              onClose={displayMode === chip.mode ? () => setDisplayMode(undefined) : undefined}
              testID={chip.testID}>
              {chip.label}
            </Chip>
          ))}
          <Chip
            icon="calendar"
            selected={Boolean(dateRange)}
            onPress={() => setDatePickerVisible(true)}
            onClose={dateRange ? () => setDateRange(undefined) : undefined}
            testID="components.Map.Search.DateRangeChip">
            {dateFilterLabel}
          </Chip>
        </ScrollView>
        <RangeCalendarModal
          range={dateRange}
          visible={datePickerVisible}
          onDismiss={() => setDatePickerVisible(false)}
          onSelect={setDateRange}
          testID="components.Map.Search.DateRangeCalendar"
        />
      </View>
      {searchText ? (
        <Surface style={[styles.resultListContainer, { backgroundColor: appTheme.colors.card }]}>
          <FlatList
            data={searchResults}
            renderItem={renderSearchResult}
            keyExtractor={getSearchResultKey}
            ItemSeparatorComponent={() => <Divider />}
            keyboardShouldPersistTaps="always"
          />
          {showNoResults ? <Caption style={styles.noResultsText}>No results</Caption> : null}
        </Surface>
      ) : null}
    </View>
  );
}

function getSearchPlaceholder(searchTargets: SearchTargets) {
  const targets = [
    searchTargets.users ? 'people' : undefined,
    searchTargets.groups ? 'groups' : undefined,
    searchTargets.places ? 'places' : undefined,
  ].filter(Boolean);

  return `Search ${targets.join(', ') || 'places'}`;
}

const styles = StyleSheet.create({
  noResultsText: {
    marginHorizontal: 8,
    marginVertical: 7,
  },
  resultDetail: {
    marginVertical: 0,
  },
  resultIcon: {
    flexShrink: 0,
    marginRight: 10,
  },
  resultListContainer: {
    borderRadius: 8,
    elevation: 4,
    marginHorizontal: 5,
    maxHeight: 220,
    ...Platform.select({
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.16,
        shadowRadius: 8,
      },
      web: {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.16)',
      },
    }),
  },
  resultRow: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: 10,
  },
  resultText: {
    flex: 1,
    marginVertical: 0,
  },
  resultTextContainer: {
    flex: 1,
  },
  searchBar: {
    margin: 5,
    marginTop: 10,
  },
  selectedFilterContent: {
    gap: 6,
    paddingHorizontal: 5,
    paddingVertical: 4,
  },
});
